import { CLIENT_SHIFT_TYPES } from "../constants/clientPortal";

/**
 * Fallback on-cost % when admin config fields are absent.
 * Canonical defaults for the shared pricing engine.
 */
export const DEFAULT_CLIENT_ON_COST_PERCENTS = {
  holidayPay: 12.07,
  ni: 15,
  pension: 3,
  levy: 0.5,
};

/**
 * Resolve the uplift percentage for an informational shift-type selection.
 * Day/weekday has no uplift; other types map 1:1 to admin config fields.
 */
export function getShiftTypeUpliftPercent(shiftType, uplifts = {}) {
  const u = uplifts || {};
  switch (shiftType) {
    case CLIENT_SHIFT_TYPES.URGENT:
      return Number(u.urgentUplift) || 0;
    case CLIENT_SHIFT_TYPES.NIGHT:
      return Number(u.nightUplift) || 0;
    case CLIENT_SHIFT_TYPES.WEEKEND:
      return Number(u.weekendUplift) || 0;
    case CLIENT_SHIFT_TYPES.BANK_HOLIDAY:
      return Number(u.bankHolidayUplift) || 0;
    case CLIENT_SHIFT_TYPES.DAY:
    default:
      return 0;
  }
}

const pctOrDefault = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Holiday pay on the basic worker rate:
 * Standard Worker Rate = Basic + (Basic × HolidayPay%)
 */
export function applyHolidayPayToRate(workerCurrentRate, uplifts = {}) {
  const base = parseFloat(workerCurrentRate) || 0;
  const holidayPayPct = pctOrDefault(
    uplifts?.holidayPay,
    DEFAULT_CLIENT_ON_COST_PERCENTS.holidayPay
  );
  const holidayPayAmount = base * (holidayPayPct / 100);
  return {
    baseRate: base,
    holidayPayPct,
    holidayPayAmount,
    rateWithHoliday: base + holidayPayAmount,
  };
}

/**
 * Apply shift-type uplift to a rate:
 * newRate = rate + (rate × uplift%)
 */
export function applyShiftTypeUplift(rate, upliftPercent) {
  const current = parseFloat(rate) || 0;
  const uplift = Number(upliftPercent) || 0;
  const upliftAmount = current * (uplift / 100);
  return {
    upliftPercent: uplift,
    upliftAmount,
    hourlyRate: current + upliftAmount,
  };
}

/**
 * Shared worker-rate steps:
 * 1) Holiday pay on basic → Standard Worker Rate
 * 2) Shift uplift on Standard → Final Worker Rate
 */
export function calculateWorkerRateWithHolidayThenUplift({
  workerBaseRate,
  shiftType,
  uplifts,
  upliftPercent: upliftPercentOverride,
}) {
  const holiday = applyHolidayPayToRate(workerBaseRate, uplifts);
  const upliftPercent =
    upliftPercentOverride != null
      ? Number(upliftPercentOverride) || 0
      : getShiftTypeUpliftPercent(shiftType, uplifts);
  const uplifted = applyShiftTypeUplift(holiday.rateWithHoliday, upliftPercent);

  return {
    baseRate: holiday.baseRate,
    holidayPayPct: holiday.holidayPayPct,
    holidayPayAmount: holiday.holidayPayAmount,
    rateWithHoliday: holiday.rateWithHoliday,
    upliftPercent: uplifted.upliftPercent,
    upliftAmount: uplifted.upliftAmount,
    workerRate: uplifted.hourlyRate,
  };
}

/**
 * Employer on-costs + margin from the final Worker Rate only.
 * Holiday must already be included in workerRate — do not re-apply.
 *
 * Margin = Worker Rate × Margin %
 * Client Rate = Worker Rate + NI + Pension + Levy + Margin
 */
export function calculateClientHourlyFromWorkerRate({
  workerRate,
  uplifts = {},
  marginPercentage = 0,
  workerBaseRate,
  holidayPayPct,
  holidayPayAmount,
}) {
  const rate = parseFloat(workerRate) || 0;
  const u = uplifts || {};

  const niPct = pctOrDefault(u.ni, DEFAULT_CLIENT_ON_COST_PERCENTS.ni);
  const pensionPct = pctOrDefault(
    u.pension,
    DEFAULT_CLIENT_ON_COST_PERCENTS.pension
  );
  const levyPct = pctOrDefault(u.levy, DEFAULT_CLIENT_ON_COST_PERCENTS.levy);
  const marginPct = Number(marginPercentage) || 0;

  const niAmount = rate * (niPct / 100);
  const pensionAmount = rate * (pensionPct / 100);
  const levyAmount = rate * (levyPct / 100);
  const marginAmount = rate * (marginPct / 100);

  const clientHourly =
    rate + niAmount + pensionAmount + levyAmount + marginAmount;

  return {
    workerBaseRate:
      workerBaseRate != null ? parseFloat(workerBaseRate) || 0 : undefined,
    workerRate: rate,
    holidayPayPct,
    holidayPayAmount,
    niPct,
    niAmount,
    pensionPct,
    pensionAmount,
    levyPct,
    levyAmount,
    marginPct,
    marginAmount,
    clientHourly,
  };
}

/**
 * Full client role rate card hourly (canonical engine).
 */
export function calculateRoleRateCardHourly({
  workerBaseRate,
  shiftType,
  uplifts,
  marginPercentage = 0,
}) {
  const worker = calculateWorkerRateWithHolidayThenUplift({
    workerBaseRate,
    shiftType,
    uplifts,
  });
  const client = calculateClientHourlyFromWorkerRate({
    workerRate: worker.workerRate,
    uplifts,
    marginPercentage,
    workerBaseRate: worker.baseRate,
    holidayPayPct: worker.holidayPayPct,
    holidayPayAmount: worker.holidayPayAmount,
  });

  return {
    ...worker,
    ...client,
    hourlyRate: client.clientHourly,
  };
}

/**
 * Worker role rate card hourly (final pay only).
 */
export function calculateWorkerRoleRateCardHourly({
  workerBaseRate,
  shiftType,
  uplifts,
}) {
  const worker = calculateWorkerRateWithHolidayThenUplift({
    workerBaseRate,
    shiftType,
    uplifts,
  });
  return {
    ...worker,
    hourlyRate: worker.workerRate,
  };
}

export function calculateHourlyRateFromBaseAndUplift(
  workerBaseRate,
  upliftPercent
) {
  const base = parseFloat(workerBaseRate) || 0;
  const uplifted = applyShiftTypeUplift(base, upliftPercent);
  return {
    baseRate: base,
    upliftPercent: uplifted.upliftPercent,
    upliftAmount: uplifted.upliftAmount,
    hourlyRate: uplifted.hourlyRate,
  };
}

/**
 * Email regression fixture (Night, 15% uplift, 20% margin).
 * Basic £12.71 → Client £22.69 (rounded to 2 dp).
 */
export function assertEmailNightPricingFixture() {
  const uplifts = {
    holidayPay: 12.07,
    nightUplift: 15,
    ni: 15,
    pension: 3,
    levy: 0.5,
  };
  const result = calculateRoleRateCardHourly({
    workerBaseRate: 12.71,
    shiftType: CLIENT_SHIFT_TYPES.NIGHT,
    uplifts,
    marginPercentage: 20,
  });
  const round2 = (n) => Math.round(n * 100) / 100;
  const expected = {
    holidayPayAmount: 1.53,
    rateWithHoliday: 14.24,
    workerRate: 16.38,
    niAmount: 2.46,
    pensionAmount: 0.49,
    levyAmount: 0.08,
    marginAmount: 3.28,
    clientHourly: 22.69,
  };
  const actual = {
    holidayPayAmount: round2(result.holidayPayAmount),
    rateWithHoliday: round2(result.rateWithHoliday),
    workerRate: round2(result.workerRate),
    niAmount: round2(result.niAmount),
    pensionAmount: round2(result.pensionAmount),
    levyAmount: round2(result.levyAmount),
    marginAmount: round2(result.marginAmount),
    clientHourly: round2(result.clientHourly),
  };
  const mismatches = Object.keys(expected).filter(
    (k) => actual[k] !== expected[k]
  );
  return { ok: mismatches.length === 0, expected, actual, mismatches };
}
